"use client";

import { useState } from "react";
import Link from "next/link";
import ProjectCard from "./ProjectCard";
import { ARCHIVE, usedCategories, filterProjects } from "../lib/projects";

const CATS = ["All", ...usedCategories()];

// Singular, lowercase noun for each category, used to build the "More
// __ work" heading below — "Websites" reads as "website work", not
// "Websites work".
const CAT_NOUN = {
  "Brand Identity": "brand identity",
  "Websites": "website",
  "Campaigns": "campaign",
  "Reports": "report",
  "Events": "event"
};

function moreWorkHeading(filter) {
  if (filter === "All") return "More work";
  return `More ${CAT_NOUN[filter] || filter.toLowerCase()} work`;
}

function colCardStyle(col, indexInCol, projectId, filterActive) {
  // MPI and Curasion's source images are 1:1 squares, which read much
  // taller than their 16:9 neighbours at full column width — and Curasion
  // was also in the col-b "bleed" slot, overhanging left toward Northleaf
  // in col-a and crowding the gutter. Shrink both; Curasion right-aligns
  // (marginLeft: auto) so the freed space sits on the left, MPI left-aligns
  // so its freed space sits on the right, toward the col-b gutter.
  if (projectId === "curasion") return { width: "68%", marginLeft: "auto" };
  if (projectId === "mpi") return { width: "68%" };
  // Discovery Fund's square listing video has the same tall-card issue, but
  // it sits in col-a next to Northleaf's report — left-align it instead so
  // the freed space sits on the right, toward the col-b gutter.
  if (projectId === "discovery-fund") return { width: "58%" };
  // Root Builders' square thumbnail has the same tall-card issue as
  // Discovery Fund above, and sits in the same column (col-a) — shrink and
  // left-align it the same way, freeing space toward the col-b gutter.
  if (projectId === "root-builders") return { width: "60%" };
  // Landed at an odd indexInCol in col-b (after Root Builders shifted everyone
  // below it), inheriting the -32px bleed. That bleed, plus MPI's own
  // marginLeft:auto right-align in col-a, left only ~8px between the two
  // cards' closest corner — exempt Viewbook from the zigzag entirely.
  if (projectId === "viewbook") return undefined;

  // The zigzag inset/bleed below was visually tuned against the specific
  // project sequence in the unfiltered "All" view. A category filter
  // reshuffles which project lands at which indexInCol, so the same
  // treatment ends up applied to a different, untuned pairing of cards —
  // e.g. Viewbook picking up Curasion's -32px bleed under "Brand Identity"
  // and crowding whatever happens to sit next to it. Only zigzag on "All";
  // filtered views render at plain full width instead.
  if (filterActive || indexInCol % 2 !== 1) return undefined;
  // Col-a's inset recedes from both edges (width 76% + marginLeft 12% = 88%,
  // never reaching the right edge), so it never touches col-b — safe as a %
  // at any width, kept exactly as original. Col-b's bleed only recedes on
  // the left, so a % there scales with viewport and can exceed the 40px
  // row-stack gap; capped as a fixed px instead, just under the gap.
  return col === "a"
    ? { width: "76%", marginLeft: "12%" }
    : { width: "calc(100% + 32px)", marginLeft: "-32px" };
}

export default function WorkIndex() {
  const [filter, setFilter] = useState("All");
  const filtered = filterProjects(filter);
  const colA = filtered.filter((_, i) => i % 2 === 0);
  const colB = filtered.filter((_, i) => i % 2 === 1);
  const filteredArchive = filterProjects(filter, ARCHIVE);

  return (
    <main id="main-content">
      <section className="px-sec" style={{ paddingTop: 88, paddingBottom: 40 }}>
        <h1 className="px-h1" style={{ fontSize: "clamp(50px, 7.2vw, 108px)", lineHeight: 1 }}>Work</h1>
        <p style={{ margin: "28px 0 0", maxWidth: 640, fontSize: 19, lineHeight: 1.55, color: "var(--px-text-secondary)" }}>
          Driven by our grit, grounded in humility, and a candid approach, we help our clients re-discover who they are.
        </p>
        <div className="px-filter-scroll" style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 44 }}>
          {CATS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              aria-pressed={filter === c}
              style={{
                flex: "0 0 auto",
                padding: "0 0 4px",
                fontSize: 12,
                fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                cursor: "pointer",
                border: "none",
                borderBottom: "1px solid " + (filter === c ? "var(--px-text)" : "transparent"),
                background: "none",
                color: filter === c ? "var(--px-text)" : "var(--px-text-tertiary)",
                whiteSpace: "nowrap"
              }}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="px-sec px-row-stack" style={{ paddingTop: 32, paddingBottom: 88 }}>
        <div className="px-feat-col">
          {colA.map((p, i) => <ProjectCard key={p.id} project={p} style={colCardStyle("a", i, p.id, filter !== "All")} />)}
        </div>
        <div className="px-feat-col px-col-b">
          {colB.map((p, i) => <ProjectCard key={p.id} project={p} style={colCardStyle("b", i, p.id, filter !== "All")} />)}
        </div>
      </section>

      {filteredArchive.length > 0 && (
        <section className="px-sec" style={{ paddingTop: 0, paddingBottom: 96 }}>
          <h2 style={{ margin: "0 0 24px", fontSize: "clamp(28px, 3vw, 40px)", letterSpacing: "-0.025em", fontWeight: 400 }}>{moreWorkHeading(filter)}</h2>
          <div className="px-fill-box" style={{ display: "flex", flexDirection: "column", padding: "8px 28px" }}>
            {filteredArchive.map((p, i) => (
              <Link
                key={p.id}
                href={`/work/${p.id}`}
                style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "baseline", padding: "22px 16px", ...(i > 0 ? { borderTop: "1px solid var(--px-border)" } : null) }}
              >
                <span style={{ fontSize: 21, fontWeight: 500, letterSpacing: "-0.012em" }}>{p.title}</span>
                <span className="px-mono-label">{p.kind}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
